import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Reveal";
import Scorecard from "../components/Scorecard";
import SeoHead from "../components/SeoHead";
import { JOURNEY, PILLARS, MANIFESTO, OFFER_PATH } from "../data/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div data-testid="home-page">
      <SeoHead
        title="Local Proof STL | Executive Operations Consulting — St. Louis"
        description="Operational Performance Assessments and implementation partnerships for established, owner-led service businesses in St. Louis."
        canonical="https://localproofstl.com/"
        ogType="website"
        ogUrl="https://localproofstl.com/"
        ogImage="https://localproofstl.com/og-image.jpg"
        twitterImage="https://localproofstl.com/og-image.jpg"
      />
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-surface">
        <div className="absolute inset-0">
          <motion.img
            src={HERO_IMG}
            alt="Modern architecture"
            style={{ y: imgY, scale: imgScale }}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/70 to-white/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/80 to-transparent" />
        </div>

        <div className="relative max-w-[1280px] mx-auto w-full px-6 lg:px-10 pb-20 lg:pb-28 pt-36">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="eyebrow mb-8"
          >
            Executive Operations Consulting · St. Louis
          </motion.p>
          <h1 className="font-serif font-light text-ink text-5xl sm:text-7xl lg:text-[5.5rem] leading-[0.94] tracking-tight max-w-5xl">
            <MaskedLines
              lines={[
                "Build a business that",
                "scales without depending",
                "on the owner.",
              ]}
            />
          </h1>
          <Reveal delay={0.6} className="mt-8 max-w-xl">
            <p className="text-lg text-charcoal/80 leading-relaxed">
              Local Proof helps established service businesses uncover operational bottlenecks,
              strengthen execution, and build the systems, visibility, and operating discipline
              needed to scale.
            </p>
          </Reveal>
          <Reveal delay={0.75} className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              data-testid="hero-book-cta"
              data-track="request-assessment-hero"
              className="group inline-flex items-center gap-2 bg-ink text-white px-7 py-4 rounded-full text-sm font-semibold hover:bg-charcoal transition-colors duration-200"
            >
              Request an Assessment
              <ArrowUpRight size={17} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/services"
              className="group inline-flex items-center gap-2 text-ink px-2 py-4 text-sm font-semibold"
            >
              Explore our services
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WHO WE HELP */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-36">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-6">Who we help</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-ink">
              The ceiling is rarely the market. It&rsquo;s the operating system.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-lg text-charcoal/80 leading-relaxed">
            <p>
              Most established service businesses hit an invisible ceiling — decisions bottleneck
              at the top, knowledge lives in people&rsquo;s heads, and performance is invisible until
              something breaks.
            </p>
            <p>
              We work with owner-led service businesses across the trades and beyond — anywhere
              margins are made and lost in scheduling discipline, quality of execution, and the
              ability to turn one-time work into recurring revenue. The principles are the same
              everywhere: rigorous operating systems let a business scale.
            </p>
          </div>
        </div>
      </section>

      {/* TRUST PILLARS MARQUEE */}
      <section className="border-y border-border bg-surface py-6 overflow-hidden" data-testid="pillars-marquee">
        <div className="marquee-track animate-marquee">
          {[...PILLARS, ...PILLARS].map((p, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-3 text-steel text-sm font-medium whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-mist" />
              {p}
            </span>
          ))}
        </div>
      </section>

      {/* WHAT WE DO — OFFER PATH BENTO */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-36">
        <Reveal className="max-w-2xl mb-14">
          <p className="eyebrow mb-6">What we do</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl leading-tight tracking-tight text-ink">
            Assess first. Implement what matters most.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {OFFER_PATH.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.1}>
              <Link
                to="/services"
                data-testid={`home-offer-${s.slug}`}
                className="group block h-full bg-white border border-border rounded-2xl p-8 lg:p-10 hover:border-ink/30 hover:shadow-[0_30px_70px_-40px_rgba(11,31,51,0.35)] transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1"
              >
                <span className="text-steel text-sm font-semibold">{s.stage}</span>
                <h3 className="font-serif text-3xl text-ink mt-4 mb-3">{s.name}</h3>
                <p className="text-charcoal/70 leading-relaxed mb-8">{s.desc}</p>
                <span className="inline-flex items-center gap-2 text-ink text-sm font-semibold">
                  Learn more
                  <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CLIENT JOURNEY */}
      <section className="bg-ink text-white relative overflow-hidden">
        <div className="noise-overlay" />
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-36">
          <Reveal className="max-w-2xl mb-16">
            <p className="eyebrow text-mist mb-6">The client journey</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl leading-tight tracking-tight">
              A deliberate path from insight to durable systems.
            </h2>
          </Reveal>
          <div className="space-y-0">
            {JOURNEY.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.05}>
                <div className="grid md:grid-cols-12 gap-4 md:gap-8 py-8 border-t border-white/10 group">
                  <div className="md:col-span-1 font-serif text-3xl text-mist group-hover:text-white transition-colors duration-300">{step.n}</div>
                  <h3 className="md:col-span-4 font-serif text-2xl">{step.title}</h3>
                  <p className="md:col-span-6 md:col-start-7 text-cloud/70 leading-relaxed">{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SCORECARD PREVIEW */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-36">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-6">Executive dashboard</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl leading-tight tracking-tight text-ink mb-6">
              See your operational health at a glance.
            </h2>
            <p className="text-charcoal/70 leading-relaxed mb-8">
              Every Operational Performance Assessment produces a Business Health Scorecard — an
              honest maturity rating across the domains that decide whether you can scale.
            </p>
            <Link
              to="/assessment"
              data-testid="home-scorecard-link"
              data-track="explore-assessment-home"
              className="group inline-flex items-center gap-2 text-ink text-sm font-semibold"
            >
              Explore the Assessment
              <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="lg:col-span-8">
            <Reveal><Scorecard /></Reveal>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="border-t border-border bg-surface py-24 lg:py-36" data-testid="manifesto">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <p className="eyebrow mb-14">How we think</p>
          <div className="space-y-3">
            {MANIFESTO.map((line, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="flex items-baseline gap-6 py-4 border-b border-border">
                  <span className="text-steel text-sm font-semibold tabular-nums shrink-0">0{i + 1}</span>
                  <p className="font-serif text-2xl sm:text-3xl lg:text-4xl text-ink leading-snug tracking-tight">{line}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      {/* TRUST SIGNAL — right before the final CTA in footer. Deliberately minimal. */}
      <section className="border-t border-border py-28 lg:py-40" data-testid="home-trust-signal">
        <div className="max-w-[1080px] mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <p className="eyebrow mb-8">Our philosophy</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink text-balance max-w-3xl mx-auto">
              Built by an operator. Designed for business owners.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-10 text-lg text-charcoal/75 leading-[1.85] max-w-2xl mx-auto text-balance">
              Local Proof STL was built on a simple belief: businesses grow when knowledge
              becomes a repeatable asset instead of remaining trapped inside individual experience.
              Our work combines operational thinking, structured systems, and AI-assisted workflows
              to help organizations capture, organize, and activate their expertise.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/about"
              data-testid="home-philosophy-cta"
              className="group inline-flex items-center gap-2 mt-14 text-ink text-sm font-semibold border-b border-ink pb-1 hover:gap-3 transition-[gap] duration-200"
            >
              Learn why we built Local Proof STL
              <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
